/**
 * 段階 2「予想する」の問題（labs.md 各ラボの「予想」、viz-step カードと同じ題材）。
 * 操作列を記録と同じ形のコードで見せ、最後の print の出力を予想させる。答えは盤面の模型から計算する。
 */
import type { LabModel, LabRun } from './types';
import { recordLines } from './codegen';
import { currentState, runOps } from './run';
import { swiftArray, swiftOptional } from './swift-format';
import { EMPTY_STACK, stackModel, type StackOp, type StackState } from './stack';
import { emptyQueue, queueModel, waiting, type QueueOp, type QueueState } from './queue';
import { EMPTY_DICT, dictModel, lookup, type DictOp, type DictState } from './dict';

export interface Puzzle<S, O> {
  /** 対応するカード（mvp-cards.md）。 */
  id: string;
  title: string;
  initial: S;
  ops: O[];
  /** 最後に足す print の行。 */
  question: string;
  /** 最後の盤面から、question の出力を計算する。 */
  answer: (state: S) => string;
}

export interface PreparedPuzzle<S> {
  id: string;
  title: string;
  /** 見せるコード（記録の行 + question）。 */
  code: string;
  /** question の行番号（1 始まり）。 */
  questionLine: number;
  run: LabRun<S>;
  expected: string;
}

export function preparePuzzle<S, O>(
  model: LabModel<S, O>,
  puzzle: Puzzle<S, O>,
): PreparedPuzzle<S> {
  const run = runOps(model, puzzle.initial, puzzle.ops);
  const lines = [...recordLines(model, run), puzzle.question];
  return {
    id: puzzle.id,
    title: puzzle.title,
    code: lines.join('\n'),
    questionLine: lines.length,
    run,
    expected: puzzle.answer(currentState(run)),
  };
}

/** 予想の比較用に整える（前後の空白、連続する空白、`,` の後の空白の有無を揃える）。 */
export function normalizeAnswer(s: string): string {
  return s
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/\[\s+/g, '[')
    .replace(/\s+\]/g, ']');
}

export function checkAnswer(input: string, expected: string): boolean {
  return normalizeAnswer(input) === normalizeAnswer(expected);
}

const push = (value: number): StackOp => ({ type: 'push', value });
const pop: StackOp = { type: 'pop' };

export const STACK_PUZZLES: Puzzle<StackState, StackOp>[] = [
  {
    id: 'sqd-a06-stack-viz-basic',
    title: '積んで、取って、また積む',
    initial: EMPTY_STACK,
    ops: [push(1), push(2), pop, push(3)],
    question: 'print(stack)',
    answer: (s) => swiftArray(s.items),
  },
  {
    id: 'sqd-a06-stack-poplast-empty',
    title: '取りすぎたとき',
    initial: EMPTY_STACK,
    ops: [push(5), push(7), pop, pop, pop, push(2)],
    question: 'print(stack.popLast(), stack.popLast())',
    answer: (s) => {
      const items = [...s.items];
      const a = items.pop();
      const b = items.pop();
      return `${swiftOptional(a)} ${swiftOptional(b)}`;
    },
  },
];

const enqueue = (value: number): QueueOp => ({ type: 'enqueue', value });
const dequeue: QueueOp = { type: 'dequeue' };

export const QUEUE_PUZZLES: Puzzle<QueueState, QueueOp>[] = [
  {
    id: 'sqd-a06-queue-viz-head',
    title: '次に通る人と head',
    initial: emptyQueue('head'),
    ops: [enqueue(1), enqueue(2), enqueue(3), dequeue, enqueue(4), dequeue],
    question: 'print(queue[head], head)',
    answer: (s) => `${s.items[s.head] ?? '?'} ${s.head}`,
  },
  {
    id: 'sqd-a06-queue-head-predict',
    title: 'まだ通っていない人',
    initial: emptyQueue('head'),
    ops: [enqueue(5), enqueue(8), dequeue, enqueue(2), dequeue, enqueue(6)],
    question: 'print(queue[head...])',
    answer: (s) => swiftArray(waiting(s)),
  },
];

const set = (key: string, value: number): DictOp => ({ type: 'set', key, value });
const inc = (key: string): DictOp => ({ type: 'increment', key });
const insert = (value: number): DictOp => ({ type: 'insert', value });

export const DICT_PUZZLES: Puzzle<DictState, DictOp>[] = [
  {
    id: 'sqd-k03-dict-viz-lockers',
    title: '上書きと、無いキー',
    initial: EMPTY_DICT,
    ops: [set('x', 5), set('y', 2), set('x', 7)],
    question: 'print(dict["x"], dict["z"])',
    answer: (s) => `${swiftOptional(lookup(s, 'x'))} ${swiftOptional(lookup(s, 'z'))}`,
  },
  {
    id: 'sqd-k03-dict-default-count',
    title: 'banana を数える',
    initial: EMPTY_DICT,
    ops: [...'banana'].map(inc),
    question: 'print(dict["a"]!, dict["b"]!, dict["n"]!)',
    answer: (s) => ['a', 'b', 'n'].map((k) => String(lookup(s, k) ?? 0)).join(' '),
  },
  {
    id: 'sqd-k03-set-insert-dup',
    title: '同じ番号を 2 回書く',
    initial: EMPTY_DICT,
    ops: [insert(3), insert(3), insert(5)],
    question: 'print(seen.count)',
    answer: (s) => String(s.seen.length),
  },
];

export const preparedStackPuzzles = () => STACK_PUZZLES.map((p) => preparePuzzle(stackModel, p));
export const preparedQueuePuzzles = () => QUEUE_PUZZLES.map((p) => preparePuzzle(queueModel, p));
export const preparedDictPuzzles = () => DICT_PUZZLES.map((p) => preparePuzzle(dictModel, p));
